// Fix: Populated file with ArtistList component
import React, { useState, useEffect } from 'react';
import { Artist, UserRole, ViewMode, Project } from '../types';
import ArtistCard from './ArtistCard';
import ArtistRow from './ArtistRow';
import ContextMenu, { ContextMenuItem } from './ContextMenu';
import ProjectAssignmentModal from './ProjectAssignmentModal';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';

interface ArtistListProps {
  artists: Artist[];
  projects: Project[];
  currentRole: string | null;
  onEditArtist: (artist: Artist) => void;
  onDeleteArtist: (artistId: string) => void;
  onAssignToProject: (artistIds: string[], projectId: string) => void;
  onInviteToProject: (artistIds: string[]) => void;
  onCreateArtist: () => void;
  viewMode: ViewMode;
  selectedArtistIds: Set<string>;
  onArtistSelect: (artistId: string, event: React.MouseEvent) => void;
}

const ArtistList: React.FC<ArtistListProps> = ({ 
  artists, 
  projects, 
  currentRole, 
  onEditArtist, 
  onDeleteArtist, 
  onAssignToProject,
  onInviteToProject,
  onCreateArtist,
  viewMode, 
  selectedArtistIds, 
  onArtistSelect 
}) => {
  const { t } = useTranslation();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; artist: Artist } | null>(null);
  const [generalContextMenu, setGeneralContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [projectAssignmentModal, setProjectAssignmentModal] = useState(false);
  const [artistsToAssign, setArtistsToAssign] = useState<string[]>([]);

  // Close context menu on click
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setGeneralContextMenu(null);
    };
    if (contextMenu || generalContextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, generalContextMenu]);

  const handleContextMenu = (e: React.MouseEvent, artist: Artist) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, artist });
    setGeneralContextMenu(null);
  };

  const handleGeneralContextMenu = (e: React.MouseEvent) => {
    // Only show if right-click on background, not on cards
    if ((e.target as HTMLElement).closest('[data-artist-card]') || 
        (e.target as HTMLElement).closest('[data-artist-row]')) {
      return;
    }
    e.preventDefault();
    setGeneralContextMenu({ x: e.clientX, y: e.clientY });
    setContextMenu(null);
  };

  const handleAssignToProject = (projectId: string) => {
    if (artistsToAssign.length > 0) {
      onAssignToProject(artistsToAssign, projectId);
      setArtistsToAssign([]);
    }
  };

  const getContextMenuItems = (artist: Artist): ContextMenuItem[] => {
    // Determine which artists to operate on (selected or just the clicked one)
    const targetArtists = selectedArtistIds.has(artist.id) && selectedArtistIds.size > 0
      ? Array.from(selectedArtistIds)
      : [artist.id];

    return [
      {
        label: t('contextMenu.edit'),
        icon: <ICONS.Edit className="w-4 h-4" />,
        onClick: () => onEditArtist(artist),
      },
      {
        separator: true,
        label: '',
        onClick: () => {},
      },
      {
        label: `${t('contextMenu.assignToProject')}${targetArtists.length > 1 ? ` (${targetArtists.length})` : ''}`,
        icon: <ICONS.Briefcase className="w-4 h-4" />,
        onClick: () => {
          setArtistsToAssign(targetArtists);
          setProjectAssignmentModal(true);
        },
      },
      {
        label: `${t('contextMenu.inviteToProject')}${targetArtists.length > 1 ? ` (${targetArtists.length})` : ''}`,
        icon: <ICONS.Mail className="w-4 h-4" />,
        onClick: () => {
          onInviteToProject(targetArtists);
        },
      },
    ];
  };
  
  if (artists.length === 0) {
    return <p className="text-center text-gray-400 mt-8">Художники не найдены. Добавьте первого!</p>;
  }

  if (viewMode === ViewMode.LIST) {
    return (
      <>
        <div 
          className="flex flex-col space-y-3"
          onContextMenu={handleGeneralContextMenu}
        >
          {artists.map(artist => (
            <div
              key={artist.id}
              data-artist-row="true"
              onContextMenu={(e) => handleContextMenu(e, artist)}
            >
              <ArtistRow
                artist={artist}
                projects={projects}
                currentRole={currentRole}
                onEdit={onEditArtist}
                onDelete={onDeleteArtist}
                isSelected={selectedArtistIds.has(artist.id)}
                onSelect={(e) => onArtistSelect(artist.id, e)}
              />
            </div>
          ))}
        </div>

        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={getContextMenuItems(contextMenu.artist)}
            onClose={() => setContextMenu(null)}
          />
        )}

        {generalContextMenu && (
          <ContextMenu
            x={generalContextMenu.x}
            y={generalContextMenu.y}
            items={[
              {
                label: t('contextMenu.createArtist'),
                icon: <ICONS.Plus className="w-4 h-4" />,
                onClick: onCreateArtist,
              },
            ]}
            onClose={() => setGeneralContextMenu(null)}
          />
        )}

        <ProjectAssignmentModal
          isOpen={projectAssignmentModal}
          onClose={() => setProjectAssignmentModal(false)}
          projects={projects}
          artistCount={artistsToAssign.length}
          onAssign={handleAssignToProject}
        />
      </>
    );
  }

  return (
    <>
      <div 
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6"
        onContextMenu={handleGeneralContextMenu}
      >
        {artists.map(artist => (
          <div
            key={artist.id}
            data-artist-card="true"
            onContextMenu={(e) => handleContextMenu(e, artist)}
          >
            <ArtistCard
              artist={artist}
              projects={projects}
              currentRole={currentRole}
              onEdit={onEditArtist}
              onDelete={onDeleteArtist}
              isSelected={selectedArtistIds.has(artist.id)}
              onSelect={(e) => onArtistSelect(artist.id, e)}
            />
          </div>
        ))}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems(contextMenu.artist)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {generalContextMenu && (
        <ContextMenu
          x={generalContextMenu.x}
          y={generalContextMenu.y}
          items={[
            {
              label: t('contextMenu.createArtist'),
              icon: <ICONS.Plus className="w-4 h-4" />,
              onClick: onCreateArtist,
            },
          ]}
          onClose={() => setGeneralContextMenu(null)}
        />
      )}

      <ProjectAssignmentModal
        isOpen={projectAssignmentModal}
        onClose={() => setProjectAssignmentModal(false)}
        projects={projects}
        artistCount={artistsToAssign.length}
        onAssign={handleAssignToProject}
      />
    </>
  );
};

export default ArtistList;